// Theme system — light / dark / system, token-driven, persisted, live-toggle.
//
// Sets `data-theme="light|dark"` on <html> (the RESOLVED theme) plus `data-theme-pref`
// (the user's choice: light|dark|system) so UI can reflect the selection. Default = system,
// which follows `prefers-color-scheme` and updates live when the OS theme changes.
//
// CSP-safe: only flips attributes; all colors live in external CSS custom properties.

import { loadSetting, saveSetting } from './store.js';

export type ThemePref = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const KEY = 'theme';
let pref: ThemePref = loadSetting<ThemePref>(KEY, 'system');
const listeners = new Set<(t: ResolvedTheme) => void>();
let mql: MediaQueryList | null = null;

function systemTheme(): ResolvedTheme {
  if (typeof matchMedia === 'undefined') return 'dark';
  return matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function resolve(p: ThemePref): ResolvedTheme {
  return p === 'system' ? systemTheme() : p;
}

function apply(): void {
  const root = document.documentElement;
  const resolved = resolve(pref);
  root.setAttribute('data-theme', resolved);
  root.setAttribute('data-theme-pref', pref);
  // Keep the UA color-scheme in sync so form controls / scrollbars match.
  root.style.colorScheme = resolved;
  listeners.forEach((fn) => fn(resolved));
}

function ensureSystemWatch(): void {
  if (typeof matchMedia === 'undefined' || mql) return;
  mql = matchMedia('(prefers-color-scheme: light)');
  const onChange = () => {
    if (pref === 'system') apply();
  };
  // addEventListener is the modern API; older Safari used addListener.
  if (typeof mql.addEventListener === 'function') mql.addEventListener('change', onChange);
  else if (typeof (mql as unknown as { addListener?: (cb: () => void) => void }).addListener === 'function')
    (mql as unknown as { addListener: (cb: () => void) => void }).addListener(onChange);
}

export const Theme = {
  /** Initialize from storage and apply to <html>. Call once at boot. */
  init(): void {
    ensureSystemWatch();
    apply();
  },
  /** The user's stored preference (light|dark|system). */
  get(): ThemePref {
    return pref;
  },
  /** The currently-rendered theme (light|dark) after resolving 'system'. */
  resolved(): ResolvedTheme {
    return resolve(pref);
  },
  /** Set the preference, persist it, and apply live. */
  set(next: ThemePref): void {
    pref = next;
    saveSetting(KEY, next);
    ensureSystemWatch();
    apply();
  },
  /** Subscribe to resolved-theme changes (e.g. to recolor a canvas). Returns unsubscribe. */
  onChange(fn: (t: ResolvedTheme) => void): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};
